import { useMemo } from "react"

import { useQuery } from "@tanstack/react-query"

import { type PluginEntry, pluginsApi, type PluginSurface } from "@/api/plugins-api"

import { usePluginQueryKey } from "./use-plugin-query-key"

export type PluginCatalogState = {
  /** The tenant enabled the store. False while it is loading or unreachable. */
  storeEnabled: boolean
  /** Every plugin the user may see (installed or installable). */
  items: PluginEntry[]
  /** Installed plugins that are offered on `surface`, sorted by the backend. */
  installed: PluginEntry[]
  isPending: boolean
  isError: boolean
}

const EMPTY: PluginEntry[] = []

/**
 * The employee plugin catalog for a chat surface.
 *
 * Shares the query key with the /plugins page (through `usePluginQueryKey`),
 * so an install, uninstall or reconnect there refreshes the footer menu too,
 * and the whole cache is dropped when the authenticated session changes.
 * Failures are surfaced, never retried in a loop: the store is additive and a
 * chat must stay usable without it.
 */
export function usePluginCatalog(
  enabled: boolean,
  surface: PluginSurface = "explorer"
): PluginCatalogState {
  const pluginKey = usePluginQueryKey()
  const query = useQuery({
    queryKey: pluginKey("catalog"),
    queryFn: pluginsApi.catalog,
    enabled,
    staleTime: 60_000,
    retry: false,
  })
  const items = query.data?.items ?? EMPTY
  const installed = useMemo(
    () => items.filter((entry) => entry.installed && entry.surfaces.includes(surface)),
    [items, surface]
  )
  return {
    storeEnabled: query.data?.enabled ?? false,
    items,
    installed,
    isPending: enabled && query.isPending,
    isError: query.isError,
  }
}
